import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import { Tooltip } from '@mui/material';

import { selectMaximumTravelDistance } from '../../../../../store/dataSelectors';
import { loadMaximumTravelDistanceDetails } from '../../../../../store/directusService';
import { searchVacanciesByDistance } from '../../../../../store/recruitmentService';
import { getNameFromId, getObjFromId } from '../../../../../utils/directusFunctions';
import { MAXIMUM_TRAVEL_DISTANCE_DETAILS_ERROR } from '../../../../../utils/formValidation/loadingErrorMessageConstants';
import { clearKeys } from '../../../../../utils/objectUtils';
import {
    ADVISER,
    hasRole,
    MANAGER,
    QUALITY,
    RECRUITMENT_MANAGER,
    SUPERUSER
} from '../../../../../utils/userRoles';
import SingleSelect from '../../../../formElements/SingleSelect';
import DynamicSearch from '../../../../search/DynamicSearch';
import LoadingSpinner from '../../../../ui/LoadingSpinner';

import VacancySearchTable from './VacancySearchTable';

import classes from '../../submissionStyles/vacancy.module.css';

const acceptedRoles = [ADVISER, MANAGER, QUALITY, RECRUITMENT_MANAGER, SUPERUSER];

const VacancySearchTableManagement = ({ isViewOnly = false }) => {
    // HOOKS
    const dispatch = useDispatch();

    // LOCAL STATE
    const [activeVacancies, setActiveVacancies] = useState([]);
    const [rows, setRows] = useState([]);
    const [firstTimeIn, setFirstTimeIn] = useState(true);
    const [keys, setKeys] = useState({ maxTravelId: '0' });
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedMaxTravelToWork, setSelectedMaxTravelToWork] = useState('');
    const [openTooltip, setOpenTooltip] = useState(false);
    const [isDisabled, setIsDisabled] = useState(true);

    // STORE STATE
    const roles = useSelector((state) => state.entities.userService.loggedInUser.roles);
    const jobSectorDetails = useSelector(
        (state) => state.entities.directusService.jobSectorDetails
    );
    const maximumTravelDistanceDetails = useSelector(selectMaximumTravelDistance);
    const currentParticipant = useSelector(
        (state) => state.entities.participantService.currentParticipant
    );
    const businesses = useSelector((state) => state.entities.recruitmentService.businesses);
    const vacanciesByDistance = useSelector(
        (state) => state.entities.recruitmentService.vacanciesByDistance
    );
    const vacanciesByDistanceMetaData = useSelector(
        (state) => state.entities.recruitmentService.vacanciesByDistanceMetaData
    );
    const loadingVacanciesByDistance = useSelector(
        (state) => state.entities.recruitmentService.loadingVacanciesByDistance
    );
    const vacanciesForServices = useSelector(
        (state) => state.entities.recruitmentService.vacanciesForServices
    );
    const usersForVacancies = useSelector((state) => state.entities.userService.usersForVacancies);

    // USE EFFECTS
    useEffect(() => {
        setKeys(clearKeys(keys));
        if (maximumTravelDistanceDetails?.length < 1) dispatch(loadMaximumTravelDistanceDetails());
    }, []);

    useEffect(() => {
        setIsDisabled(!hasRole(acceptedRoles, roles) || isViewOnly);
    }, [roles]);

    useEffect(() => {
        if (
            vacanciesForServices?.length < 1 ||
            !usersForVacancies ||
            usersForVacancies?.length < 1
        ) {
            setActiveVacancies([]);
            setRows([]);
            return;
        }

        const searchOwnerVacancies = vacanciesForServices.map((el) => {
            const user = usersForVacancies.find((entry) => entry.id === el?.ownerId);
            return {
                ...el,
                businessName: getNameFromId(businesses, el.businessRecordId),
                adviser: user ? `${user.firstName} ${user.lastName}` : ' - ',
                jobSectorName: getNameFromId(jobSectorDetails, el.sectorId),
                payRate: `£${el.payRateFrom} - £${el.payRateTo}`,
                postcodeOrRemote: el.remote ? 'Remote' : el.postcode
            };
        });
        setActiveVacancies(searchOwnerVacancies);
        setRows(searchOwnerVacancies);
    }, [vacanciesForServices, usersForVacancies]);

    useEffect(() => {
        if (!selectedMaxTravelToWork) return;
        if (
            vacanciesByDistance?.length &&
            !loadingVacanciesByDistance &&
            'number' in vacanciesByDistanceMetaData &&
            !vacanciesByDistanceMetaData?.last
        ) {
            dispatch(
                searchVacanciesByDistance(
                    {
                        latitude: currentParticipant.latitude,
                        longitude: currentParticipant.longitude,
                        maximumTravelDistanceId: selectedMaxTravelToWork
                    },
                    vacanciesByDistanceMetaData.number + 1
                )
            );
        } else if (vacanciesByDistanceMetaData?.last && vacanciesByDistance?.length < 1) {
            setRows([]);
        } else if (vacanciesByDistanceMetaData?.last && vacanciesByDistance?.length > 0) {
            const searchOwnerVacancies = vacanciesByDistance.map((el) => {
                const owner = usersForVacancies.find((entry) => entry.id === el?.ownerId);
                return {
                    ...el,
                    businessName: getNameFromId(businesses, el.businessRecordId),
                    adviser: owner ? `${owner.firstName} ${owner.lastName}` : '',
                    jobSectorName: getNameFromId(jobSectorDetails, el.sectorId),
                    payRate: `£${el.payRateFrom} - £${el.payRateTo}`,
                    postcodeOrRemote: el.remote ? 'Remote' : el.postcode
                };
            });
            setRows(searchOwnerVacancies);
        }
    }, [vacanciesByDistance, vacanciesByDistanceMetaData, loadingVacanciesByDistance]);

    useEffect(() => {
        if (firstTimeIn) {
            setFirstTimeIn(false);
            return;
        }
        const searchRows = !searchTerm
            ? activeVacancies
            : activeVacancies.filter((el) =>
                  el.title.toLowerCase().includes(searchTerm.toLowerCase())
              );
        setRows(searchRows);
    }, [searchTerm]);

    // HELPER FNS
    const loadingError = () => {
        if (maximumTravelDistanceDetails?.length < 1) return MAXIMUM_TRAVEL_DISTANCE_DETAILS_ERROR;
    };

    // EVENT HANDLERS
    const onMaximumTravelToWorkChange = (chosenId) => {
        if (!chosenId) {
            chosenId = '';
            setRows(activeVacancies);
        } else {
            dispatch(
                searchVacanciesByDistance(
                    {
                        latitude: currentParticipant.latitude,
                        longitude: currentParticipant.longitude,
                        maximumTravelDistanceId: chosenId
                    },
                    0
                )
            );
        }
        setSelectedMaxTravelToWork(chosenId);
    };

    const onSearch = (searchRes) => setSearchTerm(searchRes);

    const onTooltipClose = () => {
        if (!currentParticipant.postcode) setOpenTooltip(false);
    };

    const onTooltipOpen = () => {
        if (!currentParticipant.postcode) setOpenTooltip(true);
    };

    // AWAITING CONTENT
    const errorMsg = loadingError();
    if (errorMsg) return <LoadingSpinner content={errorMsg} />;

    // RENDER
    return (
        <>
            <div className={classes.vacanciesToolBar}>
                <DynamicSearch
                    id="vacancy"
                    search={onSearch}
                    disabled={
                        !!selectedMaxTravelToWork || (!rows.length && !searchTerm) || isDisabled
                    }
                    searchTerm={searchTerm}
                    placeholder="Enter Search Term"
                />
                <Tooltip
                    open={openTooltip}
                    onClose={onTooltipClose}
                    onOpen={onTooltipOpen}
                    title="Maximum travel to work cannot be used as the Participant has no post code"
                    placement="left-start">
                    <div className={classes.distanceToWork}>
                        <SingleSelect
                            id={'maxTravelId'}
                            key={keys.maxTravelId}
                            label={'Maximum Travel To Work'}
                            placeholder="Select maximum travel to work..."
                            disabled={isDisabled || searchTerm || !currentParticipant.postcode}
                            menuItems={maximumTravelDistanceDetails}
                            selectedId={selectedMaxTravelToWork}
                            selected={getObjFromId(
                                maximumTravelDistanceDetails,
                                selectedMaxTravelToWork
                            )}
                            sort={false}
                            onChange={(chosenId) => onMaximumTravelToWorkChange(chosenId)}
                        />
                    </div>
                </Tooltip>
            </div>
            {rows.length < 1 ? (
                <LoadingSpinner content="No vacancies found" />
            ) : (
                <VacancySearchTable rows={rows} searchTerm={searchTerm} />
            )}
        </>
    );
};

VacancySearchTableManagement.propTypes = {
    isViewOnly: PropTypes.bool
};

export default VacancySearchTableManagement;
